import { Add, Cancel, TurnRightRounded } from "@mui/icons-material";
import { Grid, IconButton, useTheme } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import AcxButton from "components/UI/AcxButton";
import { FilterLabel } from "components/Conversations/Views/Drawer/shared";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { ModulePreview } from "components/Admin/Organizations/types/Module.type";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import type { QuestionResultFilter as QuestionResultFilterModel } from "stores/ApplicationFilters/ApplicationFiltersStore";
import {
    compareAnswerOptionsByModuleOrder,
    isMultipleChoiceQuestion,
} from "models/AnswerType";
import { useStore } from "utils/useStore";
import type { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";

type QuestionResultFilterProps = {
    variant?: "signals" | "conversations";
} & IApplicationFilterComponent;

// These selects sit low in the filter drawer, and the tag select grows as
// results are chosen, which pushes the controls even lower. Passing the same
// value as both the min and max menu height makes "auto" placement flip the
// menu above as soon as it can't fully fit below, rather than rendering
// downward and clipping options.
const MENU_HEIGHT_PX = 260;

type ModuleOptionGroup = {
    label: string;
    options: QuestionResultModuleOption[];
};

type QuestionResultModuleOption = ModulePreview & {
    questionResultModuleLabel: string;
};

type QuestionResultRowProps = {
    row: QuestionResultFilterModel;
    groupedModuleOptions: ModuleOptionGroup[];
    moduleOptions: QuestionResultModuleOption[];
    isModulesLoading: boolean;
    /** Question ids already used by other rows on this row's module. */
    excludedQuestionIds: ReadonlySet<string>;
    onLoadModuleOptions: () => void;
    onChange: (next: QuestionResultFilterModel) => void;
    onRemove: () => void;
};

const QuestionResultRow = observer(function (props: QuestionResultRowProps) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const theme = useTheme();

    const { licensedModuleId: selectedModuleId, questionId: selectedQuestionId } =
        props.row;
    const selectedTagIds = useMemo(
        () => props.row.tagIds ?? [],
        [props.row.tagIds],
    );

    const moduleDetails = selectedModuleId
        ? valuesStore.moduleDetailsById.get(selectedModuleId)
        : undefined;
    const isModuleDetailsLoading =
        !!selectedModuleId &&
        !moduleDetails &&
        valuesStore.getTaskLoading(
            ApplicationFilterValuesStore.Tasks.LOAD_MODULE_DETAILS,
        );

    const questionOptions = useMemo(() => {
        return (moduleDetails?.questions ?? []).filter(
            (question) =>
                isMultipleChoiceQuestion(question) &&
                // A question can only be filtered on once per module. Its own
                // selection stays in the list so the control renders a label.
                (question.id === selectedQuestionId ||
                    !props.excludedQuestionIds.has(question.id)),
        );
    }, [moduleDetails, props.excludedQuestionIds, selectedQuestionId]);

    const selectedQuestion = useMemo(
        () =>
            questionOptions.find(
                (question) => question.id === selectedQuestionId,
            ),
        [questionOptions, selectedQuestionId],
    );

    // Sorted the way the module configures the answer options, matching the group-by editor and the
    // order the chart's bars come out in. Without it the same question lists its options differently
    // depending on which drawer you opened.
    const tagOptions = useMemo(
        () =>
            [...(selectedQuestion?.tags ?? [])]
                .filter((tag) => tag.isActive !== false)
                .sort(compareAnswerOptionsByModuleOrder),
        [selectedQuestion],
    );

    const selectedTags = useMemo(
        () => tagOptions.filter((tag) => selectedTagIds.includes(tag.id)),
        [tagOptions, selectedTagIds],
    );

    const selectedModuleOption = useMemo(
        () =>
            props.moduleOptions.find(
                (module) => module.id === selectedModuleId,
            ),
        [props.moduleOptions, selectedModuleId],
    );

    const tagSelectStyles = useMemo(
        () => ({
            container: (provided) => ({
                ...provided,
                minWidth: 0,
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
            }),
            control: (provided) => ({
                ...provided,
                minWidth: 0,
                maxWidth: "100%",
            }),
            valueContainer: (provided) => ({
                ...provided,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
            }),
        }),
        [],
    );

    return (
        <Grid
            container
            item
            direction="row"
            alignItems="flex-start"
            sx={{
                gap: 1,
                flexWrap: "nowrap",
                backgroundColor: theme.palette.lightgray.background,
                padding: 1,
                borderRadius: 1,
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
            }}
        >
            <Grid
                container
                item
                direction="column"
                rowGap={1}
                sx={{ minWidth: 0, width: "100%" }}
            >
                <Grid item sx={{ minWidth: 0, width: "100%" }}>
                    <AcxSelectSingle<QuestionResultModuleOption>
                        id="question-result-module"
                        fullWidth
                        options={props.groupedModuleOptions}
                        defaultValue={selectedModuleOption}
                        labelField="questionResultModuleLabel"
                        valueField="id"
                        placeholder="Select Module..."
                        isLoading={props.isModulesLoading}
                        maxMenuHeightPx={MENU_HEIGHT_PX}
                        minMenuHeightPx={MENU_HEIGHT_PX}
                        onMenuOpen={props.onLoadModuleOptions}
                        onChange={(item) => {
                            if (!item?.id) return;
                            if (item.id === selectedModuleId) return;

                            props.onChange({
                                licensedModuleId: item.id,
                                questionId: "",
                                tagIds: [],
                            });
                        }}
                    />
                </Grid>
                <Grid
                    container
                    item
                    columnGap={1}
                    flexWrap="nowrap"
                    alignItems="center"
                    sx={{ minWidth: 0, width: "100%" }}
                >
                    <Grid item>
                        <TurnRightRounded
                            sx={(theme) => ({
                                color: theme.palette.gray[400],
                                transform: "scale(1, -1)",
                            })}
                        />
                    </Grid>
                    <Grid item sx={{ minWidth: 0, flex: 1 }}>
                        <AcxSelectSingle
                            id="question-result-question"
                            fullWidth
                            options={questionOptions}
                            defaultValue={selectedQuestion ?? null}
                            labelField="shortQuestionText"
                            valueField="id"
                            placeholder={
                                selectedModuleId
                                    ? "Select Question..."
                                    : "Select a module first"
                            }
                            isDisabled={!selectedModuleId}
                            isLoading={isModuleDetailsLoading}
                            maxMenuHeightPx={MENU_HEIGHT_PX}
                            minMenuHeightPx={MENU_HEIGHT_PX}
                            onChange={(item) => {
                                if (!selectedModuleId) return;

                                props.onChange({
                                    licensedModuleId: selectedModuleId,
                                    questionId: item?.id ?? "",
                                    tagIds: [],
                                });
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    columnGap={1}
                    flexWrap="nowrap"
                    alignItems="center"
                    sx={{ minWidth: 0, width: "100%", overflow: "hidden" }}
                >
                    <Grid item>
                        <TurnRightRounded
                            sx={(theme) => ({
                                color: theme.palette.gray[400],
                                transform: "scale(1, -1)",
                            })}
                        />
                    </Grid>
                    <Grid item sx={{ minWidth: 0, flex: 1 }}>
                        <AcxSelectMulti
                            id="question-result-tags"
                            fullWidth
                            options={tagOptions}
                            defaultValue={selectedTags}
                            labelField="value"
                            valueField="id"
                            placeholder={
                                selectedQuestionId
                                    ? "Select Result(s)..."
                                    : "Select a question first"
                            }
                            isDisabled={!selectedQuestionId}
                            maxMenuHeightPx={MENU_HEIGHT_PX}
                            minMenuHeightPx={MENU_HEIGHT_PX}
                            containerHeight="auto"
                            maxContainerHeight="96px"
                            alignControls="flex-start"
                            isClearable={true}
                            customStyle={tagSelectStyles}
                            onChange={(items) => {
                                if (!selectedModuleId || !selectedQuestionId)
                                    return;

                                const nextTagIds = (items ?? [])
                                    .map((tag: { id?: string }) => tag.id)
                                    .filter((id): id is string => !!id);

                                props.onChange({
                                    licensedModuleId: selectedModuleId,
                                    questionId: selectedQuestionId,
                                    tagIds: nextTagIds,
                                });
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <IconButton size="small" onClick={props.onRemove}>
                    <Cancel fontSize="small" />
                </IconButton>
            </Grid>
        </Grid>
    );
});

function getModuleDisplayLabel(module: ModulePreview) {
    const description = module.description?.trim();

    if (!description) return module.name;

    return `${module.name} - ${description}`;
}

export const QuestionResultFilter = observer(function (
    props: QuestionResultFilterProps,
) {
    const { variant = "signals" } = props;
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const theme = useTheme();

    const isSignalsVariant = variant === "signals";
    const isConversationsVariant = variant === "conversations";

    const pendoId =
        variant === "signals"
            ? "signals-filter-question-result"
            : "convo-explorer-filter-question-result";

    const questionResults = props.store.questionResults ?? [];

    const [isAddingResult, setIsAddingResult] = useState(
        questionResults.length === 0,
    );

    const selectedModuleIdsKey = questionResults
        .map((row) => row.licensedModuleId)
        .join("||");

    // The module list is otherwise lazy loaded on the first menu open, which
    // leaves the select blank when the page is refreshed with this filter
    // already applied. When modules are selected, load the list and each
    // module's details up front so the saved selections render. Both loaders
    // no-op when the values are already cached or in flight.
    useEffect(() => {
        const moduleIds = selectedModuleIdsKey
            .split("||")
            .filter((moduleId) => moduleId.length > 0);
        if (moduleIds.length === 0) return;

        valuesStore.loadQuestionResultFilterableModules();
        Array.from(new Set(moduleIds)).forEach((moduleId) =>
            valuesStore.loadModuleDetails(moduleId),
        );
    }, [selectedModuleIdsKey, valuesStore]);

    useEffect(() => {
        setIsAddingResult(questionResults.length === 0);
    }, [questionResults.length]);

    const loadModuleOptions = () => {
        valuesStore.loadQuestionResultFilterableModules();
    };

    const moduleOptions = useMemo(
        () =>
            valuesStore.questionResultFilterableModules.map((module) => ({
                ...module,
                questionResultModuleLabel: getModuleDisplayLabel(module),
            })),
        [valuesStore.questionResultFilterableModules],
    );
    const isModulesLoading = valuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_QUESTION_RESULT_MODULES,
    );

    const groupedModuleOptions = useMemo(() => {
        const groups = new Map<string, QuestionResultModuleOption[]>();
        const getCategoryName = (module: ModulePreview) =>
            module.moduleCategory?.name ?? "Uncategorized";

        [...moduleOptions]
            .sort((left, right) => {
                const categoryCompare = getCategoryName(left).localeCompare(
                    getCategoryName(right),
                );
                if (categoryCompare !== 0) return categoryCompare;
                return left.questionResultModuleLabel.localeCompare(
                    right.questionResultModuleLabel,
                );
            })
            .forEach((module) => {
                const groupName = getCategoryName(module);
                const group = groups.get(groupName) ?? [];
                group.push(module);
                groups.set(groupName, group);
            });

        return Array.from(groups.entries()).map(([label, options]) => ({
            label,
            options,
        }));
    }, [moduleOptions]);

    // A row is only usable once it names a module, a question, and at least one
    // result. Blocking the add button until then keeps rows uniquely
    // identifiable by module + question.
    const hasIncompleteRow = questionResults.some(
        (row) =>
            !row.licensedModuleId ||
            !row.questionId ||
            row.tagIds.length === 0,
    );
    const canAddRow = !isAddingResult && !hasIncompleteRow;

    // Memoized on the row signature so each row keeps a stable Set identity
    // across renders, which keeps its option/tag memos from recomputing.
    const rowSignature = questionResults
        .map((row) => `${row.licensedModuleId}::${row.questionId}`)
        .join("||");
    const excludedQuestionIdsByIndex = useMemo(
        () =>
            questionResults.map(
                (row, rowIndex) =>
                    new Set(
                        questionResults
                            .filter(
                                (other, index) =>
                                    index !== rowIndex &&
                                    other.licensedModuleId ===
                                        row.licensedModuleId &&
                                    other.questionId.length > 0,
                            )
                            .map((other) => other.questionId),
                    ),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rowSignature],
    );

    const replaceRow = (
        rowIndex: number,
        next: QuestionResultFilterModel,
    ) => {
        props.store.setQuestionResults(
            questionResults.map((row, index) =>
                index === rowIndex ? next : row,
            ),
        );
    };

    const removeRow = (rowIndex: number) => {
        const remaining = questionResults.filter(
            (_, index) => index !== rowIndex,
        );
        props.store.setQuestionResults(remaining);
        if (remaining.length === 0) setIsAddingResult(true);
    };

    const addRowForModule = (licensedModuleId: string) => {
        setIsAddingResult(false);
        props.store.setQuestionResults([
            ...questionResults,
            { licensedModuleId, questionId: "", tagIds: [] },
        ]);
    };

    return (
        <Grid
            id={props.id}
            container
            item
            direction="column"
            rowGap={1}
            sx={{ minWidth: 0, width: "100%" }}
            data-pendo-id={pendoId}
        >
            {isConversationsVariant && (
                <Grid
                    container
                    item
                    width="100%"
                    flexWrap="nowrap"
                    justifyContent="space-between"
                    alignItems="center"
                    columnGap={1}
                >
                    <Grid item>
                        <FilterLabel>Question Results</FilterLabel>
                    </Grid>
                    <Grid item>
                        <AcxButton
                            color="white"
                            onClick={() => setIsAddingResult(true)}
                            sx={(theme) => ({
                                padding: "1px",
                                borderRadius: theme.shape.borderRadius / 4,
                                width: "20px",
                                height: "20px",
                                minWidth: "0px",
                                fontSize: "18px",
                                color: theme.palette.gray[400],
                            })}
                            disabled={!canAddRow}
                        >
                            <Add fontSize="inherit" />
                        </AcxButton>
                    </Grid>
                </Grid>
            )}
            {questionResults.map((row, index) => (
                <QuestionResultRow
                    key={`${index}::${row.licensedModuleId}::${row.questionId}`}
                    row={row}
                    groupedModuleOptions={groupedModuleOptions}
                    moduleOptions={moduleOptions}
                    isModulesLoading={isModulesLoading}
                    excludedQuestionIds={excludedQuestionIdsByIndex[index]}
                    onLoadModuleOptions={loadModuleOptions}
                    onChange={(next) => replaceRow(index, next)}
                    onRemove={() => removeRow(index)}
                />
            ))}
            {isAddingResult && (
                <Grid
                    container
                    item
                    direction="row"
                    alignItems="flex-start"
                    sx={{
                        gap: 1,
                        flexWrap: "nowrap",
                        backgroundColor: theme.palette.lightgray.background,
                        padding: 1,
                        borderRadius: 1,
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                    <Grid item sx={{ minWidth: 0, width: "100%" }}>
                        <AcxSelectSingle<QuestionResultModuleOption>
                            id="question-result-module"
                            fullWidth
                            options={groupedModuleOptions}
                            labelField="questionResultModuleLabel"
                            valueField="id"
                            placeholder="Select Module..."
                            isLoading={isModulesLoading}
                            maxMenuHeightPx={MENU_HEIGHT_PX}
                            minMenuHeightPx={MENU_HEIGHT_PX}
                            onMenuOpen={loadModuleOptions}
                            onChange={(item) => {
                                if (!item?.id) return;
                                addRowForModule(item.id);
                            }}
                        />
                    </Grid>
                    {questionResults.length > 0 && (
                        <Grid item>
                            <IconButton
                                size="small"
                                onClick={() => setIsAddingResult(false)}
                            >
                                <Cancel fontSize="small" />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>
            )}
            {isSignalsVariant && (
                <AcxButton
                    color="white"
                    startIcon={<Add />}
                    containerStyle={{
                        display: "inline-flex",
                        width: "fit-content",
                    }}
                    sx={(theme) => ({ color: theme.palette.primary.main })}
                    onClick={() => setIsAddingResult(true)}
                    disabled={!canAddRow}
                >
                    Question Results
                </AcxButton>
            )}
        </Grid>
    );
});
